// src/screens/admin/CotizadorCapturas.jsx
// Botón "Llenar con capturas" de una fila del Cotizador. Subes 1 a 6 capturas (Comprarviagem,
// Speedmilhas, Resumo do pedido, Buscamilhas), la IA las lee, y antes de tocar nada te muestra
// "Esto leí" para que lo confirmes. Capturas del mismo vuelo se juntan en una fila; vuelos
// distintos van a filas distintas. Lo que no se lee con certeza queda vacío: nada se inventa.
import React, { useState, useRef } from 'react';
import { toast } from '../../components/UI';
import { supabase } from '../../services/supabase';
import { fileToBase64Jpeg } from '../../services/imageGenService';
import { agruparLecturas, planDeFilas } from '../../services/capturasVuelo';
import { fmtBRL } from '../../services/cotizadorCalc';
import { btnChico } from './CotizadorUI';

const EXTRACT_URL = '/.netlify/functions/extract-flight-cotizador';
const MAX_CAPTURAS = 6;

async function leerCaptura(file, token) {
  const { base64, mediaType } = await fileToBase64Jpeg(file, 1600, 0.85);
  const res = await fetch(EXTRACT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ image: base64, mediaType }),
  });
  const d = await res.json().catch(() => ({}));
  if (res.status === 403) throw new Error('403');
  return res.ok ? d : { error: d.error || 'No se pudo leer.' };
}

export default function CotizadorCapturas({ op, onAplicar }) {
  const [leyendo, setLeyendo] = useState('');
  const [plan, setPlan] = useState(null);
  const [sinLeer, setSinLeer] = useState([]);
  const [aplicando, setAplicando] = useState(false);
  const input = useRef(null);

  const elegir = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    if (files.length > MAX_CAPTURAS) { toast(`Máximo ${MAX_CAPTURAS} capturas a la vez.`, 'error'); return; }
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token || '';
      let hechas = 0;
      setLeyendo(`Leyendo 0 de ${files.length}…`);
      const lecturas = await Promise.all(files.map(async (f) => {
        const l = await leerCaptura(f, token).catch((err) => (err.message === '403' ? { error: 'No autorizado.' } : { error: 'No se pudo leer.' }));
        hechas += 1;
        setLeyendo(`Leyendo ${hechas} de ${files.length}…`);
        return l;
      }));
      const { grupos, sinLeer: malas } = agruparLecturas(lecturas);
      if (!grupos.length) { toast('No pude leer ningún vuelo en esas capturas. Llénalo a mano.', 'error'); setLeyendo(''); return; }
      setSinLeer(malas);
      setPlan(planDeFilas(grupos, op));
    } catch { toast('No se pudieron leer las capturas.', 'error'); }
    setLeyendo('');
  };

  const aplicar = async () => {
    setAplicando(true);
    await onAplicar(plan);
    setAplicando(false);
    setPlan(null);
  };

  return (
    <>
      <button type="button" onClick={() => input.current?.click()} disabled={!!leyendo} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}>
        {leyendo || '📷 Llenar con capturas'}
      </button>
      <input ref={input} type="file" accept="image/*" multiple onChange={elegir} style={{ display: 'none' }} />

      {plan && (
        <div role="dialog" aria-modal="true" aria-label="Esto leí" style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: 16, boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', margin: '0 0 4px' }}>Esto leí</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Revisa que coincida con tus capturas. Lo que no se pudo leer queda vacío y lo completas a mano.
            </p>

            {plan.map((p, i) => (
              <div key={p.g.clave} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 12, marginBottom: 10 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange)', margin: '0 0 4px' }}>
                  VUELO {i + 1} · {p.enActual ? 'va en esta fila' : p.patch.dia_vuelo ? `fila nueva del ${p.patch.dia_vuelo.slice(8)}/${p.patch.dia_vuelo.slice(5, 7)}` : 'fila nueva'}
                </p>
                {p.resumen.map((t) => <p key={t} style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', margin: '0 0 2px' }}>{t}</p>)}
                {p.precios.length > 0 ? p.precios.map((pr) => (
                  <p key={pr.fuente} style={{ fontSize: 13, color: 'var(--off-white)', margin: '2px 0 0' }}>
                    {pr.fuente}: <b>{pr.valor !== null ? `R$ ${fmtBRL(pr.valor)}` : `${pr.millas.toLocaleString('pt-BR')} millas`}</b>
                  </p>
                )) : <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0' }}>Sin precio leído.</p>}
                {p.avisos.map((a) => <p key={a} style={{ fontSize: 12, color: 'var(--gold)', margin: '6px 0 0', lineHeight: 1.4 }}>⚠ {a}</p>)}
              </div>
            ))}
            {sinLeer.length > 0 && (
              <p style={{ fontSize: 12, color: 'var(--gold)', margin: '0 0 10px' }}>
                No pude leer {sinLeer.length === 1 ? 'la captura' : 'las capturas'} {sinLeer.join(', ')}.
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button type="button" className="btn-primary" disabled={aplicando} onClick={aplicar} style={{ flex: 2, fontSize: 14 }}>
                {aplicando ? 'Cargando…' : `Cargar ${plan.length} ${plan.length === 1 ? 'vuelo' : 'vuelos'}`}
              </button>
              <button type="button" disabled={aplicando} onClick={() => setPlan(null)} style={{ ...btnChico, flex: 1, fontSize: 13 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
